import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";

import { EyeCloseIcon, EyeOpenIcon } from "@/assets/icons";
import ColorBasedSelect from "@/components/dataset/datasetList/ColorBasedSelect";
import ColorScaleSelect from "@/components/dataset/datasetList/ColorScaleSelect";
import { DatasetStyleSelection } from "@/components/dataset/datasetList/DatasetStyleSelection/DatasetStyleSelection";
import { DatasetTypeIcon } from "@/components/dataset/datasetList/DatasetTypeIcon";
import OpacitySlider from "@/components/dataset/datasetList/OpacitySlider";
import SpatialHeader from "@/components/spatialAnalysis/header";
import { Accordion } from "@/components/ui/accordion";
import { Button } from "@/components/ui/button";
import { MAX_RANKING_INPUT } from "@/constants/dataset";
import { useMap } from "@/hooks/map/core/useMapContext";
import { useDataset } from "@/hooks/map/useDataset";
import { useAppFeatures } from "@/hooks/useAppFeatures";
import { useStepValue } from "@/hooks/useStepValue";
import { RootState } from "@/store";
import {
  setShowComparisonPopup,
  toggleDatasetExplorerPopup
} from "@/store/slice/uiSlice";
import { EColorLabel, EColorNames } from "@/types/dataset";
import { getAvailableDatasets } from "@/utils/dataset";

import { twMerge } from "tailwind-merge";

export default function MapPreviewMenu() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const { datasetData, datasetMetadataSchema, poiParameterState } =
    location?.state || {};
  const [step, setStep] = useState<number>(10);

  const { isNewUIDatasetConfigEnabled, isRankingLabelEnabled } =
    useAppFeatures();

  const [searchParams] = useSearchParams();
  const { datasets, selectedDatasetExplorer, rawDatasets } = useSelector(
    (state: RootState) => state.dataset
  );
  const availableDatasets = getAvailableDatasets(datasets);
  const { map } = useMap();

  const {
    handleToggleDatasetVisibility,
    handleChangeSelectedCategoryDataset,
    handleChangeColorDataset,
    handleAddSpatialAnalysisDataset,
    handleDisplayName,
    handleDisplayNameVisibility,
    handleDisplayNameColor,
    handleRankConfigChange,
    handleChangeStrokeWidthDataset,
    handleChangeStrokeAttributeDataset
  } = useDataset();
  const { handleResetDatasetToHistoryDataset } = useMap();

  const datasetId = searchParams.get("id") || "";
  const isExplorerMode = searchParams.get("explorer") === "true";
  const isPoiMode = searchParams.get("poi") === "true";

  const data = availableDatasets[datasetId] || {};

  const handleClosePreview = () => {
    handleResetDatasetToHistoryDataset();
    dispatch(
      setShowComparisonPopup({
        isShow: false,
        isFromAnalysisResult: false,
        isLimitShow: false
      })
    );
    navigate(`/`);
  };

  const handleBackToTableView = () => {
    handleResetDatasetToHistoryDataset();
    dispatch(
      setShowComparisonPopup({
        isShow: false,
        isFromAnalysisResult: false,
        isLimitShow: false
      })
    );
    if (isExplorerMode) {
      navigate("/", {
        state: { datasetExplorerState: selectedDatasetExplorer }
      });
      dispatch(toggleDatasetExplorerPopup(true));
    } else if (isPoiMode) {
      navigate("/", {
        state: { poiParameterState }
      });
      dispatch(toggleDatasetExplorerPopup(true));
    } else {
      navigate(`/dataset-management/${datasetId}`);
    }
  };

  const totalRows = rawDatasets[data?.data?.id]?.rows.length || 0;
  const { stepValue, maxStepValue, setStepValue } = useStepValue({
    totalRows
  });

  useEffect(() => {
    if (!datasetData || !datasetMetadataSchema || !map) return;
    handleAddSpatialAnalysisDataset(datasetData, datasetMetadataSchema, true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [datasetData, datasetMetadataSchema, map]);

  if (!data || !Object.keys(data)?.length) return null;

  // Pre-calculate strokeAttribute to avoid recalculating it in callbacks
  const strokeAttribute = data.categories.find(
    (cat) => cat.attribute_name === data.style.stroke?.attribute_name
  );

  const handleStepValueChange = (step: number) => {
    setStep(step);
    if (step >= 2 && step <= 10) {
      handleChangeSelectedCategoryDataset(
        data,
        data.selectedCategory.attribute_name,
        data.style.colorFamily,
        step
      );
    }
  };

  const handleStrokeStepValueChange = (step: number) => {
    setStep(step);
    if (step >= 2 && step <= 10) {
      handleChangeStrokeAttributeDataset(
        data,
        data.style.stroke?.attribute_name || "",
        data.style.colorFamily,
        step
      );
    }
  };

  const handleCheckDisplayLabel = (e: boolean) => {
    handleDisplayNameVisibility(data, e);
  };

  const handleSelectCategoryLabel = (selectedCategory: string) => {
    const category = data.categories.find(
      (category) => category.attribute_name === selectedCategory
    );
    handleDisplayName(data, category);
  };

  const handleChangeLabelColor = (color: string) => {
    handleDisplayNameColor(data, color as EColorLabel);
  };

  const maxRankingInput = Math.min(
    rawDatasets[data.data.id]?.rows.length || 0,
    MAX_RANKING_INPUT
  );

  const handleChangeMaxRankingInput = (newValue: number) => {
    if (newValue > maxRankingInput) newValue = maxRankingInput;
    if (newValue < 1) newValue = 1;

    handleRankConfigChange(data.data.id, data.style, {
      maxRanking: newValue
    });
  };

  const handleChangeMaxRankingSlider = (arrayValue: number[]) => {
    let value = arrayValue[0];
    if (value < 1) value = 1;

    handleRankConfigChange(data.data.id, data.style, {
      maxRanking: value
    });
  };

  return (
    <div
      className={twMerge(
        "absolute top-[30px] left-[16px] z-[9] border-gainsboro bg-white rounded-lg w-[338px] max-h-[90vh] overflow-hidden",
        "flex flex-col",
        Object.keys(availableDatasets).length > 0 ? "" : "min-h-[224px]"
      )}
    >
      <SpatialHeader title="Preview in Map" onClose={handleClosePreview} />
      <div className="p-4 flex flex-col gap-3">
        <div>
          <p className="text-grey text-xs mb-1">Selected Dataset</p>
          <div className="h-full flex flex-row items-center gap-2 border border-gainsboro rounded-xl py-2 px-3">
            <div className="flex flex-row items-center gap-2 overflow-hidden">
              {<DatasetTypeIcon type={data.properties.spatialType} />}
              <p className="text-[12px] text-txtHeading font-regular truncate">
                {data.data.name}
              </p>
            </div>

            <button
              data-testid="visibility-button"
              aria-label={
                data.properties.isVisible
                  ? `Hide dataset ${data.data.name}`
                  : `Show dataset ${data.data.name}`
              }
              className="flex bg-white items-center justify-center px-1 ml-auto"
              onClick={() => handleToggleDatasetVisibility(data.data.id)}
            >
              {data.properties.isVisible ? (
                <EyeOpenIcon className="fill-dim-gray" />
              ) : (
                <EyeCloseIcon className="fill-dim-gray" />
              )}
            </button>
          </div>
        </div>
        {isNewUIDatasetConfigEnabled ? (
          <Accordion type="single" collapsible className="flex flex-col gap-2">
            <DatasetStyleSelection
              type="fill"
              data={data}
              isLayerVisible={data.properties.isVisible}
              isEditMode={true}
              isSpatialAnalysis={false}
              isNewUIDatasetConfigEnabled={isNewUIDatasetConfigEnabled}
              categoryConfig={{
                value: data.selectedCategory?.attribute_name,
                onChange: (value) => {
                  handleChangeSelectedCategoryDataset(
                    data,
                    value,
                    data.style.colorFamily
                  );
                }
              }}
              colorConfig={{
                value: data.style.colorFamily,
                onChange: (selectedColorFamily) => {
                  handleChangeColorDataset(
                    data.data.id,
                    selectedColorFamily as EColorNames,
                    data.style.colorRanges,
                    data.selectedCategory.attribute_name,
                    data.properties.spatialType,
                    data.selectedCategory.data_type
                  );
                },
                step: step,
                onStepChange: handleStepValueChange
              }}
              totalRows={totalRows}
            />

            <DatasetStyleSelection
              type="label"
              data={data}
              totalRows={totalRows}
              isEditMode={true}
              isLayerVisible={data.properties.isVisible}
              isSpatialAnalysis={false}
              isRankingLabelEnabled={isRankingLabelEnabled}
              categoryConfig={{
                value: data.style.labelConfig?.attribute_name || "",
                onChange: handleSelectCategoryLabel,
                title: "Column Option"
              }}
              colorConfig={{
                value: data.style.labelConfig?.color_scale,
                onChange: handleChangeLabelColor
              }}
              labelConfig={{
                show: data.style.labelConfig.show_label,
                onCheck: handleCheckDisplayLabel
              }}
              rankConfig={{
                onCheck: (value) => {
                  handleRankConfigChange(data.data.id, data.style, {
                    isEnabled: value
                  });
                },
                onChangeInput: handleChangeMaxRankingInput,
                onChangeSlider: handleChangeMaxRankingSlider
              }}
            />

            <DatasetStyleSelection
              type="stroke"
              isNewUIDatasetConfigEnabled={isNewUIDatasetConfigEnabled}
              data={data}
              totalRows={totalRows}
              isLayerVisible={data.properties.isVisible}
              isEditMode={true}
              isSpatialAnalysis={false}
              isRankingLabelEnabled={isRankingLabelEnabled}
              categoryConfig={{
                value: data.style.stroke?.attribute_name || "",
                onChange: (value) => {
                  handleChangeStrokeAttributeDataset(
                    data,
                    value,
                    data.style.colorFamily,
                    data.style.colorSteps || 10
                  );
                },
                title: "Column Option"
              }}
              colorConfig={{
                value: data.style.colorFamily,
                onChange: (selectedColorFamily) => {
                  handleChangeColorDataset(
                    data.data.id,
                    selectedColorFamily as EColorNames,
                    data.style.colorRanges,
                    data.style.stroke?.attribute_name ||
                      data.selectedCategory.attribute_name,
                    data.properties.spatialType,
                    strokeAttribute?.data_type ||
                      data.selectedCategory.data_type
                  );
                },
                step: step,
                onStepChange: handleStrokeStepValueChange
              }}
              strokeConfig={{
                value: data.style.stroke.width,
                onChange: (width: number) => {
                  handleChangeStrokeWidthDataset(data.data.id, width);
                }
              }}
            />
          </Accordion>
        ) : (
          <div className=" border-gainsboro pt-2 flex flex-col gap-2">
            <ColorBasedSelect
              value={data.selectedCategory.attribute_name}
              onValueChange={(value) =>
                handleChangeSelectedCategoryDataset(
                  data,
                  value,
                  data.style.colorFamily
                )
              }
              categories={data.categories}
              disabled={!data.properties.isVisible}
            />
            <ColorScaleSelect
              value={data.style.colorFamily}
              onValueChange={(colorFamily) => {
                handleChangeColorDataset(
                  data.data.id,
                  colorFamily as EColorNames,
                  data.style.colorRanges,
                  data.selectedCategory.attribute_name,
                  data.properties.spatialType,
                  data.selectedCategory.data_type
                );
              }}
              stepValue={stepValue}
              maxStepValue={maxStepValue}
              onStepValueChange={setStepValue}
            />
            <OpacitySlider data={data} isSpatialAnalysis={false} />
          </div>
        )}
        <div className="mt-4 w-full">
          <Button className="w-full" onClick={handleBackToTableView}>
            Back to Table View
          </Button>
        </div>
      </div>
    </div>
  );
}
